/**
 * STRUCTURED DATA (JSON-LD) — generated from the CMS blocks in `lib/content.ts`,
 * never hand-typed, so a host editing the season, locations or FAQ in the
 * portal keeps the structured data true automatically. Each builder returns a
 * plain object (or array of objects); routes render it with `<JsonLd>`
 * (`components/JsonLd.tsx`) as an inline `<script type="application/ld+json">`
 * in the JSX tree — same pattern as apps/site's FAQ/Course JSON-LD — so it
 * mounts and unmounts with the route itself and can never leak between pages
 * or duplicate on re-render.
 */
import { SITE_URL, SITE_NAME } from "./seo";
import type { Block } from "./content";
import { sortSessionsByDate } from "./season";

export const ORG_ID = `${SITE_URL}/#org`;

/** Site-relative path ("/images/x.jpg") or absolute URL → always absolute. */
export function absoluteUrl(path: string): string {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/* ───────────────────────────── Organization / WebSite ───────────────────────────── */

export function organizationJsonLd(contact: Block<"contact">) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: SITE_NAME,
    alternateName: ["Join Connect", "Connect"],
    url: `${SITE_URL}/`,
    logo: absoluteUrl("/images/logo-wordmark.jpg"),
    email: contact.email,
    description:
      "A ministry helping Year 12 leavers across Sydney connect with Jesus, with each other and with university Christian groups over the summer after the HSC.",
    sameAs: [contact.instagramUrl, contact.facebookUrl].filter(Boolean),
    areaServed: {
      "@type": "City",
      name: "Sydney",
    },
  };
}

export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: SITE_NAME,
    alternateName: ["Join Connect", "Connect Sydney"],
    url: `${SITE_URL}/`,
  };
}

/* ───────────────────────────────── Places ─────────────────────────────────── */

interface PostalAddressLd {
  "@type": "PostalAddress";
  streetAddress: string;
  addressLocality: string;
  addressRegion: string;
  postalCode: string;
  addressCountry: "AU";
}

/** "3 First Ave, Eastwood NSW 2122" → a PostalAddress, or null if it doesn't parse. */
export function parseAuAddress(address: string): PostalAddressLd | null {
  const m = address.trim().match(/^(.+?),\s*([A-Za-z' .]+?)\s+([A-Z]{2,3})\s+(\d{4})$/);
  if (!m) return null;
  const [, streetAddress, addressLocality, addressRegion, postalCode] = m;
  return {
    "@type": "PostalAddress",
    streetAddress: streetAddress!.trim(),
    addressLocality: addressLocality!.trim(),
    addressRegion: addressRegion!,
    postalCode: postalCode!,
    addressCountry: "AU",
  };
}

interface PlaceLd {
  "@type": "Place";
  name: string;
  address?: PostalAddressLd;
}

function placeFromLocation(l: Block<"locations">["items"][number]): PlaceLd {
  const address = parseAuAddress(l.address);
  return {
    "@type": "Place",
    name: `${l.church} (${l.region})`,
    ...(address ? { address } : {}),
  };
}

function campusTimePlace(locations: Block<"locations">): PlaceLd {
  const address = parseAuAddress(locations.campusTimeAddress);
  return {
    "@type": "Place",
    name: locations.campusTimeTitle || locations.campusTimeChurch,
    ...(address ? { address } : {}),
  };
}

/** /find-us — the three weekly venues plus the Campus Time venue, as an ItemList of Places. */
export function placesJsonLd(locations: Block<"locations">) {
  const items = [
    ...locations.items.map((l) => placeFromLocation(l)),
    campusTimePlace(locations),
  ];
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Connect Sydney locations",
    itemListElement: items.map((place, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: place,
    })),
  };
}

/* ────────────────────────────────── Events ─────────────────────────────────── */

/**
 * The Sydney UTC offset ("+11:00" / "+10:00") for a given calendar date, so
 * event start/end times are correct ISO 8601 regardless of daylight saving —
 * every 2026/27 season date happens to fall in AEDT (+11:00), but this stays
 * correct if a future season adds a date in AEST.
 */
function sydneyOffset(date: Date): string {
  try {
    const parts = new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Sydney",
      timeZoneName: "shortOffset",
    }).formatToParts(date);
    const tz = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT+11";
    const m = tz.match(/GMT([+-]\d{1,2})(?::?(\d{2}))?/);
    if (!m) return "+11:00";
    const hours = String(Math.abs(Number(m[1]))).padStart(2, "0");
    const sign = Number(m[1]) < 0 ? "-" : "+";
    return `${sign}${hours}:${m[2] ?? "00"}`;
  } catch {
    return "+11:00";
  }
}

/**
 * "4–6pm" / "4:30–6pm" / "4pm – 6:30pm" → { startHour, startMinute, endHour,
 * endMinute } in 24h time. A single trailing am/pm applies to both ends
 * (the common "4–6pm" shorthand); each end may also carry its own. Returns
 * null for anything that doesn't look like a time range, so a host typing
 * something unexpected only loses the time-of-day, not the whole event.
 */
export function parseTimeRange(
  time: string,
): { startHour: number; startMinute: number; endHour: number; endMinute: number } | null {
  const m = time
    .trim()
    .toLowerCase()
    .match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*[-–—]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);
  if (!m) return null;
  const [, sh, sm, sMeridiem, eh, em, eMeridiem] = m;
  const meridiem = eMeridiem ?? sMeridiem;
  if (!meridiem) return null;
  const startMeridiem = sMeridiem ?? meridiem;
  const to24 = (h: string, mer: string) => {
    let hour = Number(h) % 12;
    if (mer === "pm") hour += 12;
    return hour;
  };
  return {
    startHour: to24(sh!, startMeridiem),
    startMinute: sm ? Number(sm) : 0,
    endHour: to24(eh!, meridiem),
    endMinute: em ? Number(em) : 0,
  };
}

/** ISO 8601 with a Sydney offset, e.g. "2026-11-09T16:00:00+11:00". */
function isoAtSydneyTime(isoDate: string, hour: number, minute: number): string | null {
  const m = isoDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  const utcMidday = new Date(`${isoDate}T12:00:00Z`); // any time that day is fine for the offset lookup
  const offset = sydneyOffset(utcMidday);
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  return `${y}-${mo}-${d}T${hh}:${mm}:00${offset}`;
}

/**
 * /sessions — one schema.org Event per non-break session. A "session" kind
 * runs simultaneously at all three weekly venues (locations.items); a
 * "campus" kind runs at the single Campus Time venue. Breaks are not events.
 */
export function eventsJsonLd(season: Block<"season">, locations: Block<"locations">) {
  const range = parseTimeRange(season.time);
  const organizer = { "@type": "Organization", "@id": ORG_ID, name: SITE_NAME };
  const events = sortSessionsByDate(season.sessions)
    .filter((s) => s.kind !== "break")
    .map((s) => {
      const startDate = range ? isoAtSydneyTime(s.date, range.startHour, range.startMinute) : null;
      const endDate = range ? isoAtSydneyTime(s.date, range.endHour, range.endMinute) : null;
      const location =
        s.kind === "campus" ? campusTimePlace(locations) : locations.items.map((l) => placeFromLocation(l));
      return {
        "@context": "https://schema.org",
        "@type": "Event",
        name: `${s.title} — Connect ${season.name}`,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        isAccessibleForFree: true,
        ...(s.note ? { description: s.note } : {}),
        location,
        organizer,
      };
    });
  return events;
}

/* ───────────────────────────────── FAQ ─────────────────────────────────── */

export function faqJsonLd(faq: Block<"faq">) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.items
      .filter((item) => item.question && item.answer)
      .map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
  };
}
